import React, { useCallback, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { debounce } from '@zg-rentals/zdash';

import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import TimeConstants from '../../../../constants/TimeConstants';
import { petsOptions } from '../../../../constants/ListingDetailsConstants';

import { SAVE_TO_PARENT_KEY, UPDATE_CURRENT_PROPERTY_KEY } from '../../../../utils/postingUtils';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../Wizard/context';

import PropertiesActions from '../../../../actions/PropertiesActions';
import actions from '../../../../containers/CreateListingPage/actions';
import wizardActions from '../../../Wizard/actions';

import WizardAlert from '../../../WizardAlert';
import DropdownFormField from '../../FormFields/DropdownFormField';

import WizardServerError from '../../../../model/WizardServerError';

const propTypes = {};

const defaultProps = {};

// the fields that need to be saved successfully before we close the edit modal
const SUCCESS_FIELDS = ['currentCatsCount', 'currentDogsCount'];

const RoomCurrentPetsFormSectionContainer = () => {
  const dispatch = useDispatch();

  const {
    reducerWizardDispatch,
    wizardState: { isFetchingData },
  } = useContext(WizardContext);

  const {
    modularListingState: { dataToBeSavedMap, isInModalState, serverErrorObj },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const { listingAlias, roomForRent: { currentPets: { currentCatsCount = null, currentDogsCount = null } } = {} } =
    useSelector((state) => state.properties.currentProperty);

  useEffect(() => {
    actions.clearFieldDataEntry({ reducerDispatch });

    return () => {
      actions.clearFieldDataEntry({ reducerDispatch });
    };
  }, []);

  useEffect(() => {
    if (isInModalState) {
      setSaveOutsideComponent();
    }
  }, []);

  useEffect(() => {
    if (isFetchingData || hasServerError()) {
      return;
    }

    if (checkIfAllFieldsHaveValues()) {
      saveDebounced();
    }
  }, [dataToBeSavedMap.values()]);

  const checkIfAllFieldsHaveValues = () => {
    const keyIntersections = getArrayIntersection(SUCCESS_FIELDS, Array.from(dataToBeSavedMap.keys()));

    return SUCCESS_FIELDS.length === keyIntersections.length;
  };

  const getArrayIntersection = (array1, array2) => {
    return array1.filter((element) => array2.includes(element));
  };

  // TODO: check for value changes to not include in the response
  const getDataToSave = () => {
    let data = {};

    for (const valueObj of dataToBeSavedMap.values()) {
      const { fieldName, value } = valueObj;

      data = {
        ...data,
        [fieldName]: value === '' ? null : value,
      };
    }

    const response = {
      property: {
        id: listingAlias,
        roomForRent: {
          ...data,
        },
      },
    };

    // sometimes data to save is empty
    // need a better way to check real value changes at useEffect dependency dataToBeSavedMap.values
    if (Object.keys(response.property.roomForRent).length === 0) {
      return false;
    }

    return response;
  };

  const hasServerError = () => {
    return serverErrorObj.fieldName !== '';
  };

  const save = () => {
    const dataToSave = getDataToSave();

    if (dataToSave === false) {
      return;
    }

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    dispatch(PropertiesActions.saveMultipleFieldsPropertyInfo(dataToSave))
      .then(() => {
        dispatch(
          PropertiesActions.updateCurrentPropertyCurrentPets({
            ...dataToSave.property.roomForRent,
          }),
        );

        actions.clearFieldDataEntry({ reducerDispatch });
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((message) => {
        /**
         * TODO: handle errors for review edit modal
         *
         * need to refactor all implementations of saveMultipleFieldsPropertyInfo
         * and handle errors based on fieldName
         *
         * use 'multi' for now
         */
        updateServerError(message, 'multi');
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const saveDebounced = useCallback(debounce(save, TimeConstants.SELECTION_DEBOUNCE_TIME_MS), []);

  const setSaveOutsideComponent = () => {
    actions.updateAnyProperty({
      reducerDispatch,
      data: {
        saveOutsideComponent: true,
      },
    });
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  return (
    <div data-testid="GenderPreferencesWithMinAgeFields">
      {serverErrorObj.fieldName === 'multi' && (
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />
      )}

      <DropdownFormField
        inputName="currentCatsCount"
        initialValue={currentCatsCount}
        saveToParentKey={SAVE_TO_PARENT_KEY.ROOM_FOR_RENT}
        label="Cats"
        optionsList={petsOptions}
        updateCurrentPropertyKey={UPDATE_CURRENT_PROPERTY_KEY.CURRENT_PETS}
      />

      <DropdownFormField
        inputName="currentDogsCount"
        initialValue={currentDogsCount}
        saveToParentKey={SAVE_TO_PARENT_KEY.ROOM_FOR_RENT}
        label="Dogs"
        optionsList={petsOptions}
        updateCurrentPropertyKey={UPDATE_CURRENT_PROPERTY_KEY.CURRENT_PETS}
      />
    </div>
  );
};

RoomCurrentPetsFormSectionContainer.propTypes = propTypes;
RoomCurrentPetsFormSectionContainer.defaultProps = defaultProps;

export default RoomCurrentPetsFormSectionContainer;
